import React, { useEffect } from 'react';
import ReactModal from 'react-modal';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CloseIcon } from 'components/icons';
import { useDeleteSims } from 'lib/hooks/sim/sim.mutations';
import { useModalsContext } from 'views/YourSiMs/hooks';

const deleteSimFormSchema = z.object({
  simIds: z.array(z.number()),
});

type DeleteSimFormSchema = z.infer<typeof deleteSimFormSchema>;

type DeleteSimModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function DeleteSimModal({ isOpen, onClose }: DeleteSimModalProps) {
  const deleteSimsMutation = useDeleteSims();
  const { targetSim } = useModalsContext();

  const { setValue, formState, ...form } = useForm<DeleteSimFormSchema>({
    defaultValues: { simIds: [] },
    resolver: zodResolver(deleteSimFormSchema),
    reValidateMode: 'onChange',
  });

  // Updat the form field value with the target sim Id
  useEffect(() => {
    if (!targetSim) return;

    setValue('simIds', [targetSim.id], { shouldValidate: true });
  }, [setValue, targetSim]);

  const closeModalHandler = () => {
    form.reset();
    onClose();
  };

  const onSubmit = async (values: DeleteSimFormSchema) => {
    const { simIds } = values;

    try {
      await deleteSimsMutation.mutateAsync({
        ids: simIds,
      });

      closeModalHandler();
    } catch (error) {
      form.setError('simIds', { message: 'Error while deleting sim(s)' });
    }
  };

  const getErrorMessage = () =>
    formState.errors.simIds?.find?.((error) => error?.message)?.message ||
    formState.errors.simIds?.message;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={closeModalHandler}
      ariaHideApp={false}
      contentLabel="Delete Sim"
      portalClassName="react-modal"
      overlayClassName="modal"
      className="modal-dialog react-modal-dialog-sm modal-dialog-centered"
    >
      <div className="modal-content">
        <div className="react-modal-header">
          <h5 className="react-modal-title">Delete Sim</h5>

          <button
            type="button"
            className="btn react-modal-close"
            onClick={closeModalHandler}
          >
            <CloseIcon className={''} />
          </button>
        </div>

        <div className="react-modal-body">
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <p className="react-modal-subtitle mt-2">
              Are you sure you want to delete {targetSim?.simnumber} sim?
            </p>

            <div className="invalid-feedback">{getErrorMessage()}</div>

            <div className="text-right">
              <button
                className="btn btn-danger btn-lg w-150"
                type="submit"
                disabled={formState.isSubmitting || !formState.isValid}
              >
                {formState.isSubmitting ? 'Please wait...' : 'Yes, Delete'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ReactModal>
  );
}

export default DeleteSimModal;
